import noop from 'lodash/noop';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import { useCompleteFragment } from '@eluve/apollo-client';
import { Heading, Label, Stack, Text, useToast } from '@eluve/components';
import {
  ClassicSummary,
  DynamicSummary,
  dynamicArtifactTemplateSchema,
} from '@eluve/llm-outputs';
import {
  DynamicArtifactRenderer,
  SummarySectionMarkdown,
} from '@eluve/smart-blocks';
import { Translator } from '@eluve/translation';
import {
  AppointmentSummaryKeys,
  summaryKeyToSectionTitleMap,
} from '@eluve/utils';
import {
  BillingCode,
  getVendorDataMapper,
  isClassicSummary,
  isDynamicSummary,
} from '@eluve/vendor';

import { ehrDiffContentFragment } from './operations';

export const EhrDraftContent: React.FC = () => {
  const { appointmentId } = useParams() as {
    tenantId: string;
    appointmentId: string;
  };

  const appointment = useCompleteFragment({
    fragment: ehrDiffContentFragment,
    key: { id: appointmentId },
  });

  const humanSoapNote = appointment?.humanOutputs?.[0];
  const outputVariant =
    humanSoapNote?.output?.llm_output?.metadata?.output_variant;

  let ehrSignedNote: ClassicSummary | DynamicSummary | undefined;
  let signedBillingCodes: BillingCode[] | undefined;

  const externalChart = appointment?.external_charts?.[0];
  const externalEhrVendor = externalChart?.external_ehr?.vendor;

  if (externalEhrVendor) {
    const vendorProvider = getVendorDataMapper(externalEhrVendor);
    const artifactTemplate = outputVariant
      ? dynamicArtifactTemplateSchema.parse(outputVariant.template)
      : undefined;

    if (vendorProvider.convertChartToSummary && artifactTemplate) {
      const convertedSummary = vendorProvider.convertChartToSummary({
        data: externalChart?.rawData ?? {},
        template: artifactTemplate,
      });

      if (convertedSummary) {
        ehrSignedNote = convertedSummary.summary;
        signedBillingCodes = convertedSummary.billingCodes;
      }
    }
  }

  const toast = useToast();

  // Content for translation - only EHR content
  const toTranslate = {
    ehrSignedNote,
    signedBillingCodes,
  };

  const [parsedTranslatedContent, setParsedTranslatedContent] = useState<
    typeof toTranslate | null
  >(null);

  const handleTranslated = (translated: string[] | null) => {
    if (!translated || translated.length === 0) {
      setParsedTranslatedContent(null);
      return;
    }
    try {
      setParsedTranslatedContent(JSON.parse(translated[0]!));
    } catch {
      toast.error('Could not apply translation');
      setParsedTranslatedContent(null);
    }
  };

  const translatedEhrSignedNote = parsedTranslatedContent?.ehrSignedNote;
  const translatedSignedBillingCodes =
    parsedTranslatedContent?.signedBillingCodes;

  const displayEhrSignedNote = translatedEhrSignedNote ?? ehrSignedNote;
  const displaySignedBillingCodes =
    translatedSignedBillingCodes ?? signedBillingCodes;

  return (
    <Stack gap={2}>
      <Stack
        className="mt-4"
        justify="between"
        align="center"
        gap={2}
        direction="horizontal"
      >
        <Heading>EHR Draft</Heading>
      </Stack>

      <div className="mb-4 w-1/3">
        <Label>Need translation?</Label>
        <Translator
          items={[JSON.stringify(toTranslate)]}
          onTranslated={handleTranslated}
        />
      </div>

      {displayEhrSignedNote && isDynamicSummary(displayEhrSignedNote) && (
        <DynamicArtifactRenderer
          isReadonly={true}
          blocks={displayEhrSignedNote.blocks}
        />
      )}

      {displayEhrSignedNote && isClassicSummary(displayEhrSignedNote) && (
        <Stack gap={8}>
          {Object.entries(summaryKeyToSectionTitleMap).map(
            ([key, sectionTitle]) => {
              const summaryKey = key as AppointmentSummaryKeys;
              const value = displayEhrSignedNote?.data?.[summaryKey] ?? '';
              return (
                <Stack key={summaryKey} align="stretch" gap={4}>
                  <Heading size="3xl">{sectionTitle}</Heading>
                  <SummarySectionMarkdown
                    summaryKey={summaryKey}
                    sectionTitle=""
                    content={
                      typeof value === 'string'
                        ? value
                        : JSON.stringify(value, null, 2)
                    }
                    disabled={true}
                    handleContentChange={noop}
                  />
                </Stack>
              );
            },
          )}
        </Stack>
      )}

      <Stack gap={2} direction="horizontal">
        <Heading>Billing Codes</Heading>
      </Stack>
      {Boolean(displaySignedBillingCodes?.length) && (
        <Stack gap={2}>
          {displaySignedBillingCodes!.map(
            (billingCode: BillingCode, index: number) => (
              <Text key={index}>
                {billingCode.code} - {billingCode.description}
              </Text>
            ),
          )}
        </Stack>
      )}
    </Stack>
  );
};
